import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const CUSTOM_TOKEN_KEY = 'sncrp_custom_token';
const CUSTOM_TOKEN_LENGTH = 36;
const GRAPH_PROFILE_KEY = 'sncrp_graph_profile';

/** Matches legacy `AuthenticationService.getTokenTypes()` — the 3-char prefix registry for
 *  custom 36-char tokens (`{3-char type prefix}-{32-char value}`). */
const CUSTOM_TOKEN_TYPES: Record<string, string> = {
  DOC: 'DocumentDownload',
  REP: 'ReportDownload',
};

export interface GraphUserProfile {
  displayName: string | null;
  mail: string | null;
  jobTitle: string | null;
  mobilePhone: string | null;
  businessPhones: string[];
  officeLocation: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly msalService = inject(MsalService);
  private _graphProfile: GraphUserProfile | null = null;

  login(): void {
    this.msalService.instance.loginRedirect({ scopes: [environment.msal.apiScope] });
  }

  logout(): void {
    this.clearCustomToken();
    this.msalService.instance.logoutRedirect({
      postLogoutRedirectUri: environment.msal.postLogoutRedirectUri,
    });
  }

  acquireMsalToken(): Observable<AuthenticationResult> {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      // No cached account — kick off an interactive login immediately.
      this.login();
      return EMPTY;
    }

    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: [environment.msal.apiScope],
        account,
      }),
    ).pipe(
      catchError((error) => {
        // InteractionRequiredAuthError: consent/MFA/session expired.
        // BrowserAuthError 'monitor_window_timeout': hidden iframe timed out
        //   (common when third-party cookies are blocked by the browser).
        // Both cases require an interactive redirect to re-authenticate.
        const needsInteractiveLogin =
          error instanceof InteractionRequiredAuthError ||
          (error instanceof BrowserAuthError && error.errorCode === 'monitor_window_timeout');

        if (needsInteractiveLogin) {
          this.msalService.instance.acquireTokenRedirect({
            scopes: [environment.msal.apiScope],
            account,
          });
          return EMPTY; // redirect takes over; nothing more to emit
        }
        return throwError(() => error);
      }),
    );
  }

  /** Stores a custom 36-character supplier token (type = chars 0-2; value = chars 3-35). */
  setCustomToken(token: string): void {
    if (token.length !== CUSTOM_TOKEN_LENGTH) {
      throw new Error(`Custom token must be exactly ${CUSTOM_TOKEN_LENGTH} characters.`);
    }
    localStorage.setItem(CUSTOM_TOKEN_KEY, token);
  }

  getCustomToken(): string | null {
    return localStorage.getItem(CUSTOM_TOKEN_KEY);
  }

  clearCustomToken(): void {
    localStorage.removeItem(CUSTOM_TOKEN_KEY);
  }

  /** Matches legacy `AuthenticationService.parseCustomToken()` — splits a 36-char custom token
   *  into its 3-char type prefix (looked up in the registry) and the remaining value, for use in
   *  document-download URLs (e.g. Claim notification letter PDFs) that authenticate via a signed
   *  query-string token rather than the `Authorization` header. */
  parseCustomToken(token: string): { token: string; tokenType: string | null } {
    const tokenTypePrefix = token.length === CUSTOM_TOKEN_LENGTH ? token.substring(0, 3) : '';
    const tokenType = CUSTOM_TOKEN_TYPES[tokenTypePrefix] ?? null;
    return { token: token.substring(4), tokenType };
  }

  getActiveUserEmail(): string | null {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];
    return account?.username ?? null;
  }

  /** Fetches /me from Microsoft Graph with User.Read scope and caches result. */
  async loadGraphProfile(): Promise<GraphUserProfile | null> {
    if (this._graphProfile) { return this._graphProfile; }
    const stored = sessionStorage.getItem(GRAPH_PROFILE_KEY);
    if (stored) {
      try { this._graphProfile = JSON.parse(stored) as GraphUserProfile; return this._graphProfile; } catch { /* ignore */ }
    }
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];
    if (!account) { return null; }
    try {
      const result: AuthenticationResult = await this.msalService.instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account,
      });
      const response = await fetch('https://graph.microsoft.com/v1.0/me?$select=displayName,mail,jobTitle,mobilePhone,businessPhones,officeLocation,streetAddress,city,state,country,postalCode', {
        headers: { Authorization: `Bearer ${result.accessToken}` },
      });
      if (response.ok) {
        this._graphProfile = await response.json() as GraphUserProfile;
        sessionStorage.setItem(GRAPH_PROFILE_KEY, JSON.stringify(this._graphProfile));
      }
    } catch {
      // Non-blocking: profile fields will show as empty
    }
    return this._graphProfile;
  }

  get graphProfile(): GraphUserProfile | null { return this._graphProfile; }

  get fullName(): string {
    return this._graphProfile?.displayName ?? this.getActiveUserEmail() ?? '';
  }

  get jobTitle(): string { return this._graphProfile?.jobTitle ?? ''; }

  get telephone(): string {
    return (this._graphProfile?.businessPhones?.[0]) ?? '';
  }

  get mobile(): string { return this._graphProfile?.mobilePhone ?? ''; }

  get fullAddress(): string {
    const p = this._graphProfile;
    if (!p) { return ''; }
    const parts = [p.streetAddress, [p.postalCode, p.city].filter(Boolean).join(' '), p.state, p.country].filter(Boolean);
    return parts.join(', ');
  }
}
